import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import { ModalBase } from '@components/Modal';
import { Checkbox } from '@components/Checkbox';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import Alert from '@components/Toaster/Alert';
import { getFullName } from '@helpers/userName';
import { handleApiError } from '@helpers/api';
import { useTranslation, userCoversTeam } from '@hooks';
import { conversationsService } from '@services';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 480,
        '@media (max-width: 1024px)': {
            width: '100%',
        },
    },
    '& .modal-base-content': {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
});

const MoveToTeamModal = ({ open, onClose, activeConversation, messagingTeams, onMoved }) => {
    const [teamId, setTeamId] = useState('');
    const [note, setNote] = useState('');
    const [keepAsCollaborator, setKeepAsCollaborator] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const users = useSelector(state => state.account?.account?.users || []);
    const currentUser = useSelector(state => state.auth.user);

    const { t } = useTranslation();

    const teamOptions = useMemo(
        () =>
            messagingTeams
                .filter(team => team.id !== activeConversation?.teamId)
                .map(team => ({ value: team.id, label: team.name })),
        [messagingTeams, activeConversation?.teamId],
    );

    const assignee = useMemo(
        () => users.find(user => user.id === activeConversation?.assignedUserId),
        [users, activeConversation?.assignedUserId],
    );

    const selectedTeam = useMemo(() => messagingTeams.find(team => team.id === teamId), [messagingTeams, teamId]);

    const assigneeKeepsConversation = assignee && selectedTeam && userCoversTeam(assignee, selectedTeam.id);
    const actorLosesAccess = !!selectedTeam && !userCoversTeam(currentUser, selectedTeam.id);

    useEffect(() => {
        // Default-check only when the move takes the conversation out of the actor's sight; re-run per selected team.
        setKeepAsCollaborator(actorLosesAccess);
    }, [teamId]);

    const onSubmit = async () => {
        try {
            setIsSubmitting(true);
            const conversation = await conversationsService.assignTeam(activeConversation?.id, {
                teamId,
                note: note.trim() || null,
                keepAsCollaborator,
            });
            onMoved(conversation, selectedTeam);
            onClose();
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={open}
            handleClose={onClose}
            title={t('InboxPage.moveToTeamTitle')}
            onPrimaryAction={onSubmit}
            primaryActionText={t('buttons.moveConversation')}
            primaryActionDisabled={!teamId || isSubmitting}
        >
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                {t('InboxPage.moveToTeamDescription')}
            </Typography>
            <Select
                value={teamId}
                onChange={({ target: { value } }) => setTeamId(value)}
                options={teamOptions}
                label={t('common.team')}
                fullWidth
                required
            />
            {assignee && selectedTeam && (
                <Alert severity="warning">
                    {t(
                        assigneeKeepsConversation
                            ? 'InboxPage.moveToTeamAssigneeKept'
                            : 'InboxPage.moveToTeamAssigneeCleared',
                        {
                            name: getFullName(assignee.firstName, assignee.lastName),
                            team: selectedTeam.name,
                        },
                    )}
                </Alert>
            )}
            <Input
                inputProps={{ value: note, onChange: e => setNote(e.target.value) }}
                label={t('InboxPage.internalNoteOptional')}
                fullWidth
                multiline
                minRows={3}
                maxLength={2000}
            />
            <FormControlLabel
                control={
                    <Checkbox checked={keepAsCollaborator} onChange={e => setKeepAsCollaborator(e.target.checked)} />
                }
                label={<Typography variant="body2">{t('InboxPage.keepMeAsCollaborator')}</Typography>}
                sx={{ marginLeft: '-9px', marginRight: 0 }}
            />
        </Modal>
    );
};

export default MoveToTeamModal;
